import { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    Typography
} from '@mui/material';
import Navbar from './Navbar';
import {
    KOT_PRINTER_MODES,
    getDefaultPrintBehaviorSettings,
    getPrintBehaviorSettings,
    resetPrintBehaviorSettings,
    savePrintBehaviorSettings
} from '../../utils/printBehaviorSettings';

const SETTING_META = [
    {
        key: 'kot',
        title: 'KOT Print',
        description: 'Kitchen Order Ticket print enable/disable.'
    },
    {
        key: 'completeOrder',
        title: 'Complete Order Print',
        description: 'Running/complete order slip print enable/disable.'
    },
    {
        key: 'tempBill',
        title: 'Temporary Bill Print',
        description: 'Temporary bill print enable/disable.'
    },
    {
        key: 'finalBill',
        title: 'Final Bill Print',
        description: 'Final checkout bill print enable/disable.'
    }
];

const PrintBehaviorSettings = () => {
    const [settings, setSettings] = useState(() => getPrintBehaviorSettings());

    const hasChanges = useMemo(() => {
        const live = getPrintBehaviorSettings();
        return JSON.stringify(live) !== JSON.stringify(settings);
    }, [settings]);

    const handleToggle = (key) => (_event, checked) => {
        setSettings((prev) => ({
            ...prev,
            [key]: checked
        }));
    };

    const handleKotPrinterModeChange = (event) => {
        setSettings((prev) => ({
            ...prev,
            kotPrinterMode: event.target.value
        }));
    };

    const handleSave = () => {
        const saved = savePrintBehaviorSettings(settings);
        setSettings(saved);
        alert('Print behavior settings saved successfully.');
    };

    const handleReset = () => {
        const defaults = resetPrintBehaviorSettings();
        setSettings(defaults);
        alert('Print behavior settings reset to default values.');
    };

    const handleApplyDefaultsWithoutSave = () => {
        setSettings(getDefaultPrintBehaviorSettings());
    };

    return (
        <>
            <Navbar />
            <Box sx={{ p: 2 }}>
                <Card sx={{ maxWidth: 900, mx: 'auto' }}>
                    <CardContent>
                        <Typography variant="h5" fontWeight={700} gutterBottom>
                            Print Behavior Settings
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Enable only the print jobs you want. Disabled options are skipped during POS print flow.
                        </Typography>

                        <Divider sx={{ mb: 2 }} />

                        <Stack spacing={1.5}>
                            {SETTING_META.map((item) => (
                                <Box
                                    key={item.key}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1.5,
                                        p: 1.5
                                    }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={Boolean(settings[item.key])}
                                                onChange={handleToggle(item.key)}
                                            />
                                        }
                                        label={
                                            <Box>
                                                <Typography variant="subtitle1" fontWeight={600}>
                                                    {item.title}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {item.description}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                    {item.key === 'kot' && (
                                        <FormControl size="small" sx={{ mt: 1, minWidth: 260 }} disabled={!settings.kot}>
                                            <InputLabel>KOT Printer Mode</InputLabel>
                                            <Select
                                                label="KOT Printer Mode"
                                                value={settings.kotPrinterMode || KOT_PRINTER_MODES.NETWORK}
                                                onChange={handleKotPrinterModeChange}
                                            >
                                                <MenuItem value={KOT_PRINTER_MODES.NETWORK}>Network Printer</MenuItem>
                                                <MenuItem value={KOT_PRINTER_MODES.SYSTEM}>System Installed Printer</MenuItem>
                                            </Select>
                                        </FormControl>
                                    )}
                                </Box>
                            ))}
                        </Stack>

                        <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
                            <Button variant="contained" onClick={handleSave}>
                                Save Settings
                            </Button>
                            <Button variant="outlined" onClick={handleApplyDefaultsWithoutSave}>
                                Load Default (Unsaved)
                            </Button>
                            <Button variant="outlined" color="error" onClick={handleReset}>
                                Reset & Save Default
                            </Button>
                            <Typography variant="caption" sx={{ alignSelf: 'center', color: hasChanges ? 'warning.main' : 'success.main' }}>
                                {hasChanges ? 'Unsaved changes' : 'Saved'}
                            </Typography>
                        </Stack>
                    </CardContent>
                </Card>
            </Box>
        </>
    );
};

export default PrintBehaviorSettings;

